import { useState } from 'react'
import type { PasswordMode } from '@/types'
import { weightUnit, kgToDisplay, displayToKg } from '@/utils/time'
import { PBKDF2_ITERATIONS } from '@/utils/crypto'

interface OnboardingWizardProps {
  userId: string
  weight: number
  gender: 'male' | 'female'
  onComplete: (settings: {
    weight: number
    gender: 'male' | 'female'
    passwordHash?: string
    passwordSalt?: string
    passwordIterations?: number
    passwordMode?: PasswordMode
    timeoutMinutes?: number
    backupEmail?: string
    rawPassword?: string
  }) => void
}

export default function OnboardingWizard({ userId, weight: initialWeight, gender: initialGender, onComplete }: OnboardingWizardProps) {
  const [step, setStep] = useState(1)
  const totalSteps = 5

  // Step 2: Details
  const [weight, setWeight] = useState(kgToDisplay(initialWeight).toString())
  const [gender, setGender] = useState<'male' | 'female'>(initialGender)

  // Step 3: Password
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [passwordMode, setPasswordMode] = useState<PasswordMode>('on_load')
  const [timeoutMinutes, setTimeoutMinutes] = useState('5')
  const [passwordError, setPasswordError] = useState('')

  // Step 4: Email
  const [email, setEmail] = useState('')

  // Step 5: Passcode confirmation
  const [savedPasscode, setSavedPasscode] = useState(false)
  const [copiedPasscode, setCopiedPasscode] = useState(false)
  const [copiedUrl, setCopiedUrl] = useState(false)

  // Accumulated settings from password/email steps
  const [passwordSettings, setPasswordSettings] = useState<{
    passwordHash?: string
    passwordSalt?: string
    passwordIterations?: number
    passwordMode?: PasswordMode
    timeoutMinutes?: number
  }>({})
  const [emailSetting, setEmailSetting] = useState<string | undefined>()

  const handleSetPassword = async () => {
    setPasswordError('')

    if (password.length < 8) {
      setPasswordError('Password must be at least 8 characters')
      return
    }

    if (password !== confirmPassword) {
      setPasswordError('Passwords do not match')
      return
    }

    // Generate salt and PBKDF2 hash at the current strong iteration count.
    const salt = Array.from(crypto.getRandomValues(new Uint8Array(16)))
      .map(b => b.toString(16).padStart(2, '0')).join('')
    const encoder = new TextEncoder()
    const keyMaterial = await crypto.subtle.importKey(
      'raw', encoder.encode(password), 'PBKDF2', false, ['deriveBits']
    )
    const hashBuffer = await crypto.subtle.deriveBits(
      { name: 'PBKDF2', salt: encoder.encode(salt), iterations: PBKDF2_ITERATIONS, hash: 'SHA-256' },
      keyMaterial, 256
    )
    const hash = Array.from(new Uint8Array(hashBuffer))
      .map(b => b.toString(16).padStart(2, '0')).join('')

    setPasswordSettings({
      passwordHash: hash,
      passwordSalt: salt,
      passwordIterations: PBKDF2_ITERATIONS,
      passwordMode,
      timeoutMinutes: passwordMode === 'after_timeout' ? parseInt(timeoutMinutes) || 5 : undefined,
    })
    setRawPassword(password)
    setStep(4)
  }

  const handleSaveEmail = () => {
    if (email.trim()) {
      setEmailSetting(email.trim())
    }
    setStep(5)
  }

  // Store raw password for E2E encryption setup
  const [rawPassword, setRawPassword] = useState<string | undefined>()

  const handleFinish = () => {
    onComplete({
      weight: displayToKg(parseFloat(weight)) || 80,
      gender,
      ...passwordSettings,
      backupEmail: emailSetting,
      rawPassword,
    })
  }

  const handleCopy = async (text: string, setter: (v: boolean) => void) => {
    try {
      await navigator.clipboard.writeText(text)
    } catch {
      const input = document.createElement('input')
      input.value = text
      document.body.appendChild(input)
      input.select()
      document.execCommand('copy')
      document.body.removeChild(input)
    }
    setter(true)
    setTimeout(() => setter(false), 2000)
  }

  const fullUrl = `${window.location.origin}/u/${userId}`

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-[var(--bg-primary)]">
      <div className="w-full max-w-lg">
        {/* Progress indicator */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {Array.from({ length: totalSteps }, (_, i) => (
            <div
              key={i}
              className={`h-2 rounded-full transition-all duration-300 ${
                i + 1 === step
                  ? 'w-8 bg-[var(--accent-teal)]'
                  : i + 1 < step
                    ? 'w-2 bg-[var(--accent-teal)]/60'
                    : 'w-2 bg-[var(--bg-card)]'
              }`}
            />
          ))}
        </div>

        <div className="card p-6 sm:p-8">
          {/* Step 1: Welcome */}
          {step === 1 && (
            <div className="text-center space-y-6">
              <div className="text-4xl font-bold text-[var(--accent-teal)]">SSDL</div>
              <h1 className="text-2xl font-bold text-[var(--text-primary)]">Welcome to SSDL</h1>
              <p className="text-[var(--text-secondary)] leading-relaxed">
                Let's get you set up in under a minute. We just need a couple of details
                to accurately calculate your Blood Alcohol Content.
              </p>
              <button
                onClick={() => setStep(2)}
                className="btn btn-primary w-full"
              >
                Continue
              </button>
            </div>
          )}

          {/* Step 2: Your Details */}
          {step === 2 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-bold text-[var(--text-primary)] mb-1">Your Details</h2>
                <p className="text-sm text-[var(--text-secondary)]">
                  Used to calculate your Blood Alcohol Content (BAC)
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1">
                  Body Weight ({weightUnit()})
                </label>
                <input
                  type="number"
                  value={weight}
                  onChange={(e) => setWeight(e.target.value)}
                  placeholder="80"
                  className="w-full"
                  min="30"
                  max="300"
                  step="0.5"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[var(--text-secondary)] mb-2">
                  Gender
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="gender"
                      value="male"
                      checked={gender === 'male'}
                      onChange={() => setGender('male')}
                      className="w-4 h-4 accent-[var(--accent-teal)]"
                    />
                    <span className="text-[var(--text-primary)]">Male</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="gender"
                      value="female"
                      checked={gender === 'female'}
                      onChange={() => setGender('female')}
                      className="w-4 h-4 accent-[var(--accent-teal)]"
                    />
                    <span className="text-[var(--text-primary)]">Female</span>
                  </label>
                </div>
                <p className="mt-1 text-xs text-[var(--text-secondary)]">
                  Affects BAC calculation (different body water content)
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setStep(1)}
                  className="btn btn-secondary"
                >
                  Back
                </button>
                <button
                  onClick={() => {
                    const w = parseFloat(weight)
                    if (!isNaN(w) && w >= 30 && w <= 300) {
                      setStep(3)
                    }
                  }}
                  className="btn btn-primary flex-1"
                >
                  Continue
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Secure Your Log */}
          {step === 3 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-bold text-[var(--text-primary)] mb-1">Secure Your Log</h2>
                <p className="text-sm text-[var(--text-secondary)]">
                  Add a password to protect your data
                </p>
              </div>

              <div className="p-3 bg-[var(--accent-teal)]/10 border border-[var(--accent-teal)]/30 rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <svg className="w-5 h-5 text-[var(--accent-teal)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  <span className="text-[var(--accent-teal)] font-medium">Strongly Recommended</span>
                </div>
                <p className="text-xs text-[var(--text-secondary)]">
                  Setting a password enables <strong className="text-[var(--text-primary)]">end-to-end encryption</strong>. Your data will be encrypted on your device before it reaches our servers. Without a password, data is stored unencrypted.
                </p>
              </div>

              <div>
                <label className="block text-sm text-[var(--text-secondary)] mb-1">Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm text-[var(--text-secondary)] mb-1">Confirm Password</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm password"
                  className="w-full"
                />
              </div>

              {/* Password Mode */}
              <div>
                <label className="block text-sm text-[var(--text-secondary)] mb-2">
                  When to require password?
                </label>
                <div className="space-y-2">
                  <label className="flex items-center gap-3 p-3 rounded-lg bg-[var(--bg-card)] cursor-pointer hover:bg-[var(--bg-card)]/80 transition-colors">
                    <input
                      type="radio"
                      name="wizardPasswordMode"
                      checked={passwordMode === 'on_load'}
                      onChange={() => setPasswordMode('on_load')}
                      className="w-4 h-4 accent-[var(--accent-teal)]"
                    />
                    <div>
                      <p className="text-[var(--text-primary)] font-medium">Every visit</p>
                      <p className="text-xs text-[var(--text-secondary)]">Ask for password each time you open the page</p>
                    </div>
                  </label>
                  <label className="flex items-center gap-3 p-3 rounded-lg bg-[var(--bg-card)] cursor-pointer hover:bg-[var(--bg-card)]/80 transition-colors">
                    <input
                      type="radio"
                      name="wizardPasswordMode"
                      checked={passwordMode === 'after_timeout'}
                      onChange={() => setPasswordMode('after_timeout')}
                      className="w-4 h-4 accent-[var(--accent-teal)]"
                    />
                    <div>
                      <p className="text-[var(--text-primary)] font-medium">After inactivity</p>
                      <p className="text-xs text-[var(--text-secondary)]">Lock after a period of no activity</p>
                    </div>
                  </label>
                </div>
              </div>

              {passwordMode === 'after_timeout' && (
                <div>
                  <label className="block text-sm text-[var(--text-secondary)] mb-1">
                    Lock after (minutes)
                  </label>
                  <select
                    value={timeoutMinutes}
                    onChange={(e) => setTimeoutMinutes(e.target.value)}
                    className="w-full bg-[var(--bg-card)] border border-[var(--border)] rounded-lg px-4 py-2 text-[var(--text-primary)]"
                  >
                    <option value="1">1 minute</option>
                    <option value="2">2 minutes</option>
                    <option value="5">5 minutes</option>
                    <option value="10">10 minutes</option>
                    <option value="15">15 minutes</option>
                    <option value="30">30 minutes</option>
                  </select>
                </div>
              )}

              {passwordError && (
                <p className="text-sm text-[var(--bac-danger)]">{passwordError}</p>
              )}

              <div className="flex gap-3">
                <button
                  onClick={() => setStep(2)}
                  className="btn btn-secondary"
                >
                  Back
                </button>
                <button
                  onClick={handleSetPassword}
                  className="btn btn-primary flex-1"
                >
                  Set Password
                </button>
              </div>
              <div className="p-3 bg-[var(--accent-orange)]/10 border border-[var(--accent-orange)]/30 rounded-lg">
                <p className="text-xs text-[var(--accent-orange)]">
                  If you set a password and later forget it, your encrypted data cannot be recovered — not even by us. Set up a backup email in the next step as a safety net.
                </p>
              </div>

              <button
                onClick={() => { setRawPassword(undefined); setStep(4) }}
                className="w-full text-center text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
              >
                Skip for now — data will not be encrypted
              </button>
            </div>
          )}

          {/* Step 4: Backup Email */}
          {step === 4 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-bold text-[var(--text-primary)] mb-1">Backup Email</h2>
                <p className="text-sm text-[var(--text-secondary)]">
                  If you lose your passcode, we can email it to you
                </p>
              </div>

              <div className="p-3 bg-[var(--accent-orange)]/10 border border-[var(--accent-orange)]/30 rounded-lg">
                <p className="text-sm text-[var(--accent-orange)]">
                  Adding an email removes full anonymity. We'll only use it to help you recover your URL if you lose it.
                </p>
              </div>

              <div>
                <label className="block text-sm text-[var(--text-secondary)] mb-1">Email address</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="w-full"
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setStep(3)}
                  className="btn btn-secondary"
                >
                  Back
                </button>
                <button
                  onClick={handleSaveEmail}
                  className="btn btn-primary flex-1"
                >
                  {email.trim() ? 'Save Email' : 'Skip'}
                </button>
              </div>
              {email.trim() && (
                <button
                  onClick={() => { setEmail(''); setStep(5) }}
                  className="w-full text-center text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
                >
                  Skip for now
                </button>
              )}
            </div>
          )}

          {/* Step 5: Your Passcode */}
          {step === 5 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-bold text-[var(--text-primary)] mb-1">Your Passcode</h2>
                <p className="text-sm text-[var(--text-secondary)]">
                  This is the only way to access your data. Save it somewhere safe!
                </p>
              </div>

              <div className="p-3 bg-[var(--bac-danger)]/10 border border-[var(--bac-danger)]/30 rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <svg className="w-5 h-5 text-[var(--bac-danger)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <span className="text-[var(--bac-danger)] font-medium">Important</span>
                </div>
                <p className="text-xs text-[var(--text-secondary)]">
                  If you lose this passcode and haven't set up a backup email, your data cannot be recovered.
                </p>
              </div>

              {/* Passcode */}
              <div>
                <label className="block text-sm text-[var(--text-secondary)] mb-1">Your Passcode</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={userId}
                    readOnly
                    className="flex-1 bg-[var(--bg-card)] font-mono text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => handleCopy(userId, setCopiedPasscode)}
                    className="btn btn-secondary text-sm"
                  >
                    {copiedPasscode ? 'Copied!' : 'Copy'}
                  </button>
                </div>
              </div>

              {/* Full URL */}
              <div>
                <label className="block text-sm text-[var(--text-secondary)] mb-1">Your URL</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={fullUrl}
                    readOnly
                    className="flex-1 bg-[var(--bg-card)] font-mono text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => handleCopy(fullUrl, setCopiedUrl)}
                    className="btn btn-secondary text-sm"
                  >
                    {copiedUrl ? 'Copied!' : 'Copy'}
                  </button>
                </div>
              </div>

              {/* Confirmation checkbox */}
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={savedPasscode}
                  onChange={(e) => setSavedPasscode(e.target.checked)}
                  className="mt-1 w-4 h-4 accent-[var(--accent-teal)]"
                />
                <span className="text-sm text-[var(--text-primary)]">
                  I've saved my passcode and understand it's the only way to access my data
                </span>
              </label>

              <div className="flex gap-3">
                <button
                  onClick={() => setStep(4)}
                  className="btn btn-secondary"
                >
                  Back
                </button>
                <button
                  onClick={handleFinish}
                  disabled={!savedPasscode}
                  className="btn btn-primary flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Start Logging
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Step indicator text */}
        <p className="text-center text-xs text-[var(--text-secondary)] mt-4">
          Step {step} of {totalSteps}
        </p>
      </div>
    </div>
  )
}
